/**
 * Where a signed-in person goes. EP-005 P0 — role-directed sign-in.
 *
 * Every "you are signed in, now go somewhere" path ends here: /login after
 * authentication, the site root, and bare /portal. It used to be decided in
 * the router from `users.role` (admin → /admin, everyone else → /portal), and
 * that redirect fired the instant a session existed — unmounting the sign-in
 * screen before it could ask the server where the person works. Production
 * logs on 2026-09-16 showed sign-ins followed straight by the executive demo's
 * data calls and never by /emerge/workspaces.
 *
 * Order: an authorized deep link the person was sent from; then the server's
 * answer (their default, their last workspace, then priority). No workspace
 * is an explicit state, never a silent landing somewhere unrelated, and a
 * failed lookup is an error with a retry, never a guess.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { confirmSignOutWithUnsent, safeReturnSearch } from '../config/authRoutes';
import { WorkspaceIndex, workspacesApi } from '../services/workspacesApi';
import { TENANT } from '../config';

type State = 'resolving' | 'none' | 'error';

/** A deep link is considered only if it is a path on this site and not a sign-in page. */
export function safeDeepLink(from: unknown): string | null {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return null;
  // Also the other protocol-relative shapes (verification 2026-10-19): a backslash, or an encoded slash/backslash.
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (from.startsWith('/\\') || /^\/+(%2f|%5c)/i.test(from) || /[\x00-\x1f\x7f]/.test(from)) return null;
  if (/^\/(login|register|activate)(\/|$)/.test(from)) return null;
  if (from === '/' || from === '/portal' || from === '/home') return null;
  return from;
}

const under = (path: string, base: string) => path === base || path.startsWith(`${base}/`);

/**
 * Is this deep link one the person may be sent to? Workspace areas
 * (/portal, /provider, /admin) only when it falls inside a workspace they
 * hold; anything else on the site (a signing link, account security) as is.
 * The destination still enforces itself — this only stops a stale bookmark
 * dropping someone into a surface that is not theirs.
 */
export function deepLinkAllowed(path: string, index: WorkspaceIndex): boolean {
  const inWorkspaceArea = ['/portal', '/provider', '/admin'].some((b) => under(path, b));
  if (!inWorkspaceArea) return true;
  if (under(path, '/admin')) return index.admin;
  return index.workspaces.some((w) => w.available && w.key !== 'ADMIN' && under(path, w.path));
}

export default function WorkspaceHome() {
  const { isAuthenticated, isLoading, logout, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<State>('resolving');
  const [message, setMessage] = useState('');
  const asked = useRef(false);

  const st = location.state as { from?: { pathname?: string; search?: string } } | null;
  const deepLink = safeDeepLink(st?.from?.pathname);

  const resolve = useCallback(async () => {
    setState('resolving');
    const res = await workspacesApi.index();
    if (res.success && res.data) {
      if (deepLink && deepLinkAllowed(deepLink, res.data)) {
        navigate(`${deepLink}${safeReturnSearch(st?.from?.search)}`, { replace: true });
        return;
      }
      if (res.data.home) { navigate(res.data.home, { replace: true }); return; }
      setState('none');
      return;
    }
    setMessage(res.status === undefined
      ? 'Could not reach the server to find your workspace.'
      : (res.message || 'Your workspace could not be looked up.'));
    setState('error');
  }, [navigate, deepLink, st]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || asked.current) return;
    asked.current = true;
    resolve();
  }, [isLoading, isAuthenticated, resolve]);

  if (!isLoading && !isAuthenticated) return <Navigate to="/login" replace />;

  const support = TENANT.branding.supportEmail;
  const signOut = async () => {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout(); navigate('/login', { replace: true });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f6faf9] px-4 text-[#10252d]">
      {state === 'resolving' && (
        <p className="flex items-center gap-2 text-sm text-[#60747b]" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Opening your workspace…
        </p>
      )}

      {state === 'none' && (
        <section className="w-full max-w-md rounded-lg border border-[#d7e5e1] bg-white p-6" aria-labelledby="no-ws">
          <h1 id="no-ws" className="text-xl font-semibold">Your access has not been set up yet</h1>
          <p className="mt-2 text-sm leading-6 text-[#60747b]">
            You are signed in, but your account has no {TENANT.branding.name} workspace assigned.
            Nothing is wrong with your password or authenticator — an administrator needs to
            approve the role for the work you do.
          </p>
          <p className="mt-3 text-sm leading-6">
            {support
              ? <>Write to <a className="font-medium text-[#17685d] underline" href={`mailto:${support}`}>{support}</a> and say which team you work in.</>
              : 'Ask your manager to request your role.'}
          </p>
          <button type="button" onClick={signOut} className="mt-5 rounded-md border border-[#d7e5e1] px-3 py-2 text-sm font-medium">
            Sign out
          </button>
        </section>
      )}

      {state === 'error' && (
        <section className="w-full max-w-md rounded-lg border border-[#e8c9c9] bg-white p-6" role="alert">
          <h1 className="text-xl font-semibold">We could not open your workspace</h1>
          <p className="mt-2 text-sm leading-6 text-[#60747b]">{message} You are still signed in.</p>
          <div className="mt-5 flex gap-2">
            <button type="button" onClick={resolve} className="rounded-md bg-[#10252d] px-3 py-2 text-sm font-semibold text-white">Try again</button>
            <button type="button" onClick={signOut} className="rounded-md border border-[#d7e5e1] px-3 py-2 text-sm font-medium">Sign out</button>
          </div>
        </section>
      )}
    </main>
  );
}
